import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp,
  DollarSign,
  Clock,
  Package,
  ShoppingBag,
  User,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Skeleton from '../../components/common/Skeleton';
import Icon from '../../components/common/Icon';
import StatusBadge from '../../components/common/StatusBadge';
import EmptyState from '../../components/common/EmptyState';
import { useAuth } from '../../hooks/useAuth';
import { storeService } from '../../services/storeService';
import { orderService } from '../../services/orderService';
import { formatPrice } from '../../utils/priceCalculator';
import { formatRelativeTime } from '../../utils/formatRelativeTime';

const KPI = ({ icon, label, value, hint, color = 'primary' }) => (
  <Card className="p-5">
    <div className="flex items-start justify-between gap-2 mb-2">
      <span className="text-sm text-app-text-secondary">{label}</span>
      <span className={`w-9 h-9 rounded-lg bg-${color}/10 text-${color} flex items-center justify-center`}>
        <Icon as={icon} size="md" />
      </span>
    </div>
    <div className="text-2xl font-extrabold text-app-text">{value}</div>
    {hint && <div className="text-xs text-app-text-secondary mt-1">{hint}</div>}
  </Card>
);

const ResellerDashboard = () => {
  const { stores, currentStore, user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const resellerStores = useMemo(
    () => (stores || []).filter((s) => s.roleInStore === 'reseller' && s.membershipStatus === 'active'),
    [stores]
  );

  useEffect(() => {
    const storeId = currentStore?.storeId || resellerStores[0]?.storeId;
    if (!storeId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const [s, orders] = await Promise.all([
          storeService.commissionsSummary(storeId).catch(() => null),
          orderService.getAll({ limit: 5, page: 1 }).catch(() => ({ rows: [] })),
        ]);
        if (cancelled) return;
        setSummary(s);
        setRecentOrders(orders?.rows || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentStore, resellerStores]);

  if (!resellerStores.length) {
    return (
      <div className="max-w-3xl mx-auto">
        <EmptyState
          icon={ShoppingBag}
          title="Você ainda não é revendedor ativo"
          description="Candidate-se em uma loja reseller e aguarde aprovação do dono."
          action={
            <Link to="/catalogo">
              <Button>Ver lojas disponíveis</Button>
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-extrabold text-app-text">
          Olá, {user?.name?.split(' ')[0] || 'revendedor'} 👋
        </h1>
        <p className="text-sm text-app-text-secondary">
          {currentStore?.name || resellerStores[0]?.name}
        </p>
      </div>

      {/* KPIs de comissão */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="block" height={100} />
          ))
        ) : summary ? (
          <>
            <KPI
              icon={TrendingUp}
              label="Comissão do mês"
              value={formatPrice(summary.thisMonth || 0)}
              color="primary"
            />
            <KPI
              icon={CheckCircle2}
              label="Recebido (total)"
              value={formatPrice(summary.paid || 0)}
              color="success"
            />
            <KPI
              icon={Clock}
              label="A receber"
              value={formatPrice(summary.pending || 0)}
              hint="Aguardando pagamento"
              color="warning"
            />
            <KPI
              icon={DollarSign}
              label="Total gerado"
              value={formatPrice(summary.total || 0)}
              hint="Histórico"
            />
          </>
        ) : (
          <div className="col-span-full">
            <Card className="p-4 bg-info/5 border-info/30">
              <p className="text-sm text-info">
                Sem dados de comissão ainda. Comece a vender compartilhando seu catálogo.
              </p>
            </Card>
          </div>
        )}
      </div>

      {/* Shortcuts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Link to="/revendedor/painel/catalogo">
          <Card className="p-5 hover:shadow-lg transition-all h-full flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon as={Package} size="md" className="text-primary" />
              <span className="font-semibold text-app-text">Catálogo</span>
            </div>
            <ArrowRight size={16} className="text-app-text-secondary" />
          </Card>
        </Link>
        <Link to="/revendedor/painel/pagamentos">
          <Card className="p-5 hover:shadow-lg transition-all h-full flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon as={DollarSign} size="md" className="text-primary" />
              <span className="font-semibold text-app-text">Pagamentos</span>
            </div>
            <ArrowRight size={16} className="text-app-text-secondary" />
          </Card>
        </Link>
        <Link to="/revendedor/painel/perfil">
          <Card className="p-5 hover:shadow-lg transition-all h-full flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon as={User} size="md" className="text-primary" />
              <span className="font-semibold text-app-text">Perfil</span>
            </div>
            <ArrowRight size={16} className="text-app-text-secondary" />
          </Card>
        </Link>
      </div>

      {/* Últimos pedidos */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-app-text">Últimos pedidos</h2>
          <Link to="/revendedor/painel/pedidos">
            <Button size="sm" variant="ghost" rightIcon={<ArrowRight size={14} />}>
              Ver todos
            </Button>
          </Link>
        </div>
        {loading ? (
          <Skeleton variant="text" lines={4} />
        ) : recentOrders.length === 0 ? (
          <EmptyState
            icon={ShoppingBag}
            title="Sem pedidos"
            description="Compartilhe produtos do catálogo e comece a ganhar comissão."
          />
        ) : (
          <ul className="divide-y divide-app-border">
            {recentOrders.map((o) => (
              <li key={o.id}>
                <Link
                  to={`/revendedor/painel/pedidos/${o.id}`}
                  className="flex items-center gap-3 py-3 hover:bg-app-surface-hover transition-colors rounded-md px-2 -mx-2"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-app-text">Pedido #{o.id}</p>
                    <p className="text-xs text-app-text-secondary">
                      {formatRelativeTime(o.createdAt)} · Comissão: {formatPrice(o.totalCommission)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-app-text">{formatPrice(o.totalToPay)}</p>
                    <StatusBadge domain="order" status={o.status} size="sm" />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
};

export default ResellerDashboard;
